import React from 'react';
import classNames from 'classnames';
import { Icon, IconsMap } from '@nx-frontend/shared/ui/icon';
import { TippyTooltip } from '@nx-frontend/shared/ui/tooltip';
import { UiTooltipPosition } from '@nx-frontend/shared/types';
import { CreatePortalEntityType } from './create-portal-entity';

export enum CreateEntityTab {
  BLANK = 'blank',
  TEMPLATE = 'template',
  PROJECTION = 'projection',
}

export function CreateEntityTabs({
  type,
  changeTab,
  activeTab,
  hasParent,
}: {
  type: CreatePortalEntityType;
  changeTab: (tab: CreateEntityTab) => void;
  activeTab: CreateEntityTab;
  hasParent?: boolean;
}) {
  const items = [
    { title: `Blank ${type}`, type: CreateEntityTab.BLANK, hidden: false, icon: IconsMap.PROCESS },
    {
      title: 'From template',
      type: CreateEntityTab.TEMPLATE,
      disabled: type === CreatePortalEntityType.PROCESS,
      hidden: type === CreatePortalEntityType.FOLDER,
      icon: IconsMap.TEMPLATE,
    },
    {
      title: `Linked ${type}`,
      type: CreateEntityTab.PROJECTION,
      disabled: hasParent,
      hidden: type === CreatePortalEntityType.PROCESS,
      icon: type === CreatePortalEntityType.FOLDER ? IconsMap.LINKED_FOLDER : IconsMap.LINKED_DOCUMENT,
      info: (
        <span>
          You can link a folder or page from another portal. <br /> All changes in the source will be synchronized.
        </span>
      ),
      tooltip: hasParent ? 'This option is only available at the root level' : null,
    },
  ].filter((item) => !item.hidden);

  return (
    <div className="flex gap-4">
      {items.map((item) => {
        const iconColor = item.disabled ? 'text-neutral-400' : 'text-neutral-500';
        return (
          <TippyTooltip
            message={item.tooltip ?? ''}
            key={item.type}
            show={!!item.tooltip}
            position={UiTooltipPosition.TOP}
            interactive={false}
          >
            <div
              key={item.type}
              className={classNames('flex flex-1 items-center h-12 gap-2 p-2 border rounded min-w-36', {
                'border-neutral-200': activeTab !== item.type,
                'border-cyan-900': activeTab === item.type,
                'opacity-65': item.disabled,
                'cursor-pointer': !item.disabled,
                'text-neutral-400 bg-neutral-200 cursor-default': item.disabled,
              })}
              onClick={() => {
                if (!item.disabled) {
                  changeTab(item.type);
                }
              }}
            >
              <div>
                <Icon icon={item.icon} className={classNames('w-4 h-4', iconColor)} />
              </div>
              <div className="whitespace-nowrap mr-auto text-xs">{item.title}</div>
              {item.info && (
                <TippyTooltip
                  message={item.info}
                  show={!item.disabled}
                  position={UiTooltipPosition.TOP}
                  interactive={false}
                >
                  <div>
                    <Icon icon={IconsMap.QUESTION} className={classNames('w-4 h-4', iconColor)} />
                  </div>
                </TippyTooltip>
              )}
            </div>
          </TippyTooltip>
        );
      })}
    </div>
  );
}
